// src/pages/AssetReports.tsx
// Asset Reports page for qmLEO Assets Module
// Created: 2025-02-01

import React, { useState, useEffect } from 'react';
import { 
  Download, 
  AlertTriangle, 
  ClipboardCheck, 
  Package, 
  Trash2, 
  DollarSign,
  RefreshCw
} from 'lucide-react';
import { assetsAPI } from '../services/assetsAPI';
import { AssetCategory } from '../types/asset';
import {
  AssetReportType,
  AssetReportFilters,
  VerificationStatusReport,
  OverdueReport,
  InventoryReport,
  DisposalReport,
  ValueSummaryReport,
  REPORT_TYPE_LABELS,
  REPORT_TYPE_DESCRIPTIONS
} from '../types/assetReports';

type ReportData = VerificationStatusReport | OverdueReport | InventoryReport | DisposalReport | ValueSummaryReport | null;

const AssetReports: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<AssetReportType>('verification-status');
  const [reportData, setReportData] = useState<ReportData>(null);
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [categoryId, setCategoryId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [includeDisposed, setIncludeDisposed] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      const response = await assetsAPI.getCategories();
      if (response.success && response.data) {
        setCategories(response.data.categories);
      }
    };
    loadCategories();
  }, []);

  const buildFilters = (): AssetReportFilters => {
    const filters: AssetReportFilters = {};
    if (categoryId) filters.categoryId = categoryId;
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    if (includeDisposed) filters.includeDisposed = true;
    return filters;
  };

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    const filters = buildFilters();

    try {
      let response;
      switch (selectedReport) {
        case 'verification-status':
          response = await assetsAPI.getVerificationStatusReport(filters);
          break;
        case 'overdue':
          response = await assetsAPI.getOverdueReport(filters);
          break;
        case 'inventory':
          response = await assetsAPI.getInventoryReport(filters);
          break;
        case 'disposal':
          response = await assetsAPI.getDisposalReport(filters);
          break;
        case 'value-summary':
          response = await assetsAPI.getValueSummaryReport(filters);
          break;
      }

      if (response?.success && response.data) {
        setReportData(response.data as ReportData);
      } else {
        setError(response?.error?.message || 'Failed to load report');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load report';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    const filters = buildFilters();
    const response = await assetsAPI.downloadReportCSV(selectedReport, filters);
    
    if (response.success && response.data) {
      const blob = new Blob([response.data as string], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedReport}-report-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else {
      setError('Failed to download CSV');
    }
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getReportIcon = (type: AssetReportType) => {
    switch (type) {
      case 'verification-status': return <ClipboardCheck className="w-5 h-5" />;
      case 'overdue': return <AlertTriangle className="w-5 h-5" />;
      case 'inventory': return <Package className="w-5 h-5" />;
      case 'disposal': return <Trash2 className="w-5 h-5" />;
      case 'value-summary': return <DollarSign className="w-5 h-5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      verified: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800',
      new: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      disposed: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${classes[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const renderVerificationStatusReport = (data: VerificationStatusReport) => (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-900">{data.summary.total}</div>
          <div className="text-sm text-gray-500">Total Assets</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="text-2xl font-bold text-green-700">{data.summary.verified}</div>
          <div className="text-sm text-green-600">Verified</div>
        </div>
        <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
          <div className="text-2xl font-bold text-yellow-700">{data.summary.pending}</div>
          <div className="text-sm text-yellow-600">Pending</div>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="text-2xl font-bold text-red-700">{data.summary.overdue}</div>
          <div className="text-sm text-red-600">Overdue</div>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="text-2xl font-bold text-blue-700">{data.summary.new}</div>
          <div className="text-sm text-blue-600">New</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset Tag</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Verified</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.assets.map((asset, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{asset.assetTag}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.name}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.categoryName}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.location}</td>
                <td className="px-4 py-3 text-sm">{getStatusBadge(asset.verificationStatus)}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{formatDate(asset.lastVerifiedAt)}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{formatDate(asset.verificationDueDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderOverdueReport = (data: OverdueReport) => (
    <div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="text-2xl font-bold text-red-700">{data.summary.totalOverdue}</div>
          <div className="text-sm text-red-600">Overdue Assets</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-900">{formatCurrency(data.summary.totalValue)}</div>
          <div className="text-sm text-gray-500">Total Value at Risk</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset Tag</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Overdue</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.assets.map((asset, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{asset.assetTag}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.name}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.categoryName}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.location}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{formatDate(asset.verificationDueDate)}</td>
                <td className="px-4 py-3 text-sm">
                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                    {asset.daysOverdue} days
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatCurrency(asset.currentValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderInventoryReport = (data: InventoryReport) => (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-900">{data.summary.totalAssets}</div>
          <div className="text-sm text-gray-500">Total Assets</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="text-2xl font-bold text-green-700">{data.summary.activeAssets}</div>
          <div className="text-sm text-green-600">Active</div>
        </div>
        <div className="bg-gray-50 rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-700">{data.summary.disposedAssets}</div>
          <div className="text-sm text-gray-500">Disposed</div>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="text-2xl font-bold text-blue-700">{formatCurrency(data.summary.totalPurchaseValue)}</div>
          <div className="text-sm text-blue-600">Purchase Value</div>
        </div>
        <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
          <div className="text-2xl font-bold text-purple-700">{formatCurrency(data.summary.totalCurrentValue)}</div>
          <div className="text-sm text-purple-600">Current Value</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset Tag</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serial #</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Current Value</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.assets.map((asset, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{asset.assetTag}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.name}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.categoryName}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.location}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.serialNumber || '-'}</td>
                <td className="px-4 py-3 text-sm">{getStatusBadge(asset.status)}</td>
                <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatCurrency(asset.currentValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDisposalReport = (data: DisposalReport) => (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-900">{data.summary.totalDisposed}</div>
          <div className="text-sm text-gray-500">Total Disposed</div>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="text-2xl font-bold text-blue-700">{formatCurrency(data.summary.totalOriginalValue)}</div>
          <div className="text-sm text-blue-600">Original Value</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="text-2xl font-bold text-green-700">{formatCurrency(data.summary.totalDisposalAmount)}</div>
          <div className="text-sm text-green-600">Recovery Amount</div>
        </div>
        <div className="bg-gray-50 rounded-lg border p-4">
          <div className="text-sm text-gray-500 mb-2">By Method</div>
          {data.summary.byMethod.map((m, i) => (
            <div key={i} className="text-xs text-gray-600">{m.method}: {m.count}</div>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset Tag</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disposal Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disposed To</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Authorized By</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.assets.map((asset, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{asset.assetTag}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.name}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{formatDate(asset.disposedAt)}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.disposalMethod}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.disposedTo || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{asset.disposalAuthorizedBy}</td>
                <td className="px-4 py-3 text-sm text-gray-700 text-right">{formatCurrency(asset.disposalAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderValueSummaryReport = (data: ValueSummaryReport) => (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-900">{data.totals.totalAssets}</div>
          <div className="text-sm text-gray-500">Total Assets</div>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
          <div className="text-2xl font-bold text-blue-700">{formatCurrency(data.totals.totalPurchaseValue)}</div>
          <div className="text-sm text-blue-600">Purchase Value</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4">
          <div className="text-2xl font-bold text-green-700">{formatCurrency(data.totals.totalCurrentValue)}</div>
          <div className="text-sm text-green-600">Current Value</div>
        </div>
        <div className="bg-red-50 rounded-lg border border-red-200 p-4">
          <div className="text-2xl font-bold text-red-700">{formatCurrency(data.totals.depreciation)}</div>
          <div className="text-sm text-red-600">Total Depreciation</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">By Category</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Current Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.byCategory.map((cat, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-2 text-sm text-gray-700">{cat.category}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 text-right">{cat.count}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 text-right">{formatCurrency(cat.currentValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">By Funding Source</h3>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Current Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.byFundingSource.map((src, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-2 text-sm text-gray-700">{src.fundingSource}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 text-right">{src.count}</td>
                  <td className="px-3 py-2 text-sm text-gray-700 text-right">{formatCurrency(src.currentValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderReportContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading report...</span>
        </div>
      );
    }
    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <AlertTriangle className="w-5 h-5 inline mr-2" />
          {error}
        </div>
      );
    }
    if (!reportData) {
      return (
        <div className="text-center py-12 text-gray-500">
          Click &quot;Generate Report&quot; to view data.
        </div>
      );
    }
    switch (reportData.reportType) {
      case 'verification-status':
        return renderVerificationStatusReport(reportData as VerificationStatusReport);
      case 'overdue':
        return renderOverdueReport(reportData as OverdueReport);
      case 'inventory':
        return renderInventoryReport(reportData as InventoryReport);
      case 'disposal':
        return renderDisposalReport(reportData as DisposalReport);
      case 'value-summary':
        return renderValueSummaryReport(reportData as ValueSummaryReport);
      default:
        return null;
    }
  };

  const showDateFilters = selectedReport === 'disposal';
  const showIncludeDisposed = selectedReport === 'inventory';

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Asset Reports</h1>
        <p className="text-gray-600">Generate and export asset tracking reports</p>
      </div>

      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {(['verification-status', 'overdue', 'inventory', 'disposal', 'value-summary'] as AssetReportType[]).map((type) => (
            <button
              key={type}
              onClick={() => { setSelectedReport(type); setReportData(null); }}
              className={`flex flex-col items-center p-4 rounded-lg border-2 transition-colors ${
                selectedReport === type
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }`}
            >
              {getReportIcon(type)}
              <span className="text-sm font-medium mt-2">{REPORT_TYPE_LABELS[type]}</span>
            </button>
          ))}
        </div>
        <p className="text-sm text-gray-500 mt-3">{REPORT_TYPE_DESCRIPTIONS[selectedReport]}</p>
      </div>

      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.categoryId} value={cat.categoryId}>{cat.name}</option>
              ))}
            </select>
          </div>

          {showDateFilters && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div
              >
            </>
          )}

          {showIncludeDisposed && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="includeDisposed"
                checked={includeDisposed}
                onChange={(e) => setIncludeDisposed(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="includeDisposed" className="ml-2 text-sm text-gray-700">
                Include disposed assets
              </label>
            </div>
          )}

          <button
            onClick={loadReport}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </button>

          {reportData && (
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              Download CSV
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4">
        {reportData && (
          <div className="text-xs text-gray-500 mb-4">
            Generated: {new Date(reportData.generatedAt).toLocaleString()}
          </div>
        )}
        {renderReportContent()}
      </div>
    </div>
  );
};

export default AssetReports;